import React, { useState, useEffect, useRef } from 'react';
import { fetchCredits } from '../../utils/api';
import { GCP_BACKEND_URL } from '../../types';
import { parseMarkdownBold } from '../../utils/helpers';
import { LandingPageProps, AnalysisResult, LogEntry, ConversationHistoryItem, StatusData } from './types';
import { SAMPLE_QUESTIONS, FOLLOW_UP_SUGGESTIONS } from './consts';
import ThinkingSteps from './ThinkingSteps';
import './styles.scss';

/** Phone breakpoint shared with styles.scss (@media (max-width: 768px)). */
const PHONE_MQ = '(max-width: 768px)';

const usePhone = () => {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(PHONE_MQ);
    const onChange = (e: MediaQueryListEvent) => setPhone(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return phone;
};

const LandingPage: React.FC<LandingPageProps> = ({ user, setShowAuthModal, setAuthView }) => {
  const isPhone = usePhone();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<AnalysisResult | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputNotice, setInputNotice] = useState('');
  // Live backend step while an analysis runs ("Attempt 2: widening cloud threshold…").
  const [progressText, setProgressText] = useState('');
  // Every distinct step of the current run, in order — rendered as a
  // collapsible "thinking" block (ChatGPT/Claude style) and frozen onto the
  // completion bubble so it stays expandable afterwards.
  const [progressSteps, setProgressSteps] = useState<string[]>([]);
  const runStartedAtRef = useRef<number>(0);
  const progressStepsRef = useRef<string[]>([]);
  progressStepsRef.current = progressSteps;

  const pushStep = (step: string) => {
    setProgressText(step);
    setProgressSteps(prev => (prev[prev.length - 1] === step ? prev : [...prev, step]));
  };
  const [rightPanelVisible, setRightPanelVisible] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [autoFetchLogs, setAutoFetchLogs] = useState(true);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const queryTextareaRef = useRef<HTMLTextAreaElement>(null);
  const conversationHistoryRef = useRef<HTMLDivElement>(null);
  const wasLoadingRef = useRef(false);
  const [lastLogTimestamp, setLastLogTimestamp] = useState<string | null>(null);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  const loadingSteps = [
    '🛰️ Connecting to satellite data...',
    '🧠 Interpreting your request...',
    '📊 Generating analysis outputs...',
    '🗺️ Preparing maps and charts...'
  ];

  // Conversational mode states
  const [conversationMode, setConversationMode] = useState(false);
  const [conversationSessionId, setConversationSessionId] = useState<string | null>(null);
  const [conversationContext, setConversationContext] = useState<any>(null);
  const [conversationHistory, setConversationHistory] = useState<ConversationHistoryItem[]>([]);

  // Auto-fetch backend logs every 2 seconds while analysis is running or when log tab is open
  useEffect(() => {
    if (!rightPanelVisible || !autoFetchLogs || (!loading && !showLog)) return;

    const fetchBackendLogs = async () => {
      try {
        const response = await fetch(`${GCP_BACKEND_URL}/api/logs?limit=100`);
        if (!response.ok) return;

        const data = await response.json();
        if (data.success && data.logs && data.logs.length > 0) {
          const newLogs: LogEntry[] = data.logs.map((backendLog: any) => ({
            timestamp: backendLog.timestamp || new Date().toISOString(),
            displayTime: backendLog.timestamp ? new Date(backendLog.timestamp).toLocaleTimeString() : 'N/A',
            level: backendLog.severity ? backendLog.severity.toLowerCase() as 'info' | 'success' | 'error' | 'warning' : 'info',
            message: backendLog.message || '',
            source: 'backend' as const
          }));

          if (newLogs[0].timestamp !== lastLogTimestamp) {
            setLogs(newLogs);
            setLastLogTimestamp(newLogs[0].timestamp);
          }
        }
      } catch (err) {
        console.error('Auto-fetch logs error:', err);
      }
    };

    fetchBackendLogs();
    const interval = setInterval(fetchBackendLogs, 2000);
    return () => clearInterval(interval);
  }, [loading, rightPanelVisible, showLog, lastLogTimestamp, autoFetchLogs]);

  useEffect(() => {
    if (!loading) {
      setLoadingStepIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingStepIndex((prev) => (prev + 1) % loadingSteps.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [loading, loadingSteps.length]);

  useEffect(() => {
    const textarea = queryTextareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 180)}px`;
  }, [query]);

  // Auto-scroll conversation history to bottom when new messages arrive
  useEffect(() => {
    if (conversationHistoryRef.current) {
      conversationHistoryRef.current.scrollTop = conversationHistoryRef.current.scrollHeight;
    }
  }, [conversationHistory, loading, progressText]);

  // After processing via log view, automatically switch to map view when complete
  useEffect(() => {
    if (wasLoadingRef.current && !loading && showLog && selectedResult) {
      setShowLog(false);
      setShowChart(false);
    }
    wasLoadingRef.current = loading;
  }, [loading, showLog, selectedResult]);

  const addLog = (level: 'info' | 'success' | 'error' | 'warning', message: string) => {
    const timestamp = new Date().toISOString();
    const displayTime = new Date().toLocaleTimeString();
    setLogs(prev => [{ timestamp, displayTime, level, message, source: 'frontend' }, ...prev]);
  };

  /** Fill the pending (empty) assistant reply of the latest turn, or append a new assistant-only bubble. */
  const setAssistantReply = (response: string, extra: Partial<ConversationHistoryItem> = {}) => {
    setConversationHistory(prev => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      if (last && !last.response) {
        updated[updated.length - 1] = { ...last, response, ...extra };
      } else {
        updated.push({ query: '', response, timestamp: new Date(), ...extra });
      }
      return updated;
    });
  };

  /** Errors are surfaced in the chat itself so follow-ups never fail silently. */
  const failTurn = (message: string) => {
    setError(message);
    setAssistantReply(message, { kind: 'error' });
  };

  const startNewTopic = (reason?: string) => {
    setConversationMode(false);
    setConversationSessionId(null);
    setConversationContext(null);
    setConversationHistory([]);
    setRightPanelVisible(false);
    setSelectedResult(null);
    setResults([]);
    setError('');
    setInputNotice('');
    setProgressText('');
    setProgressSteps([]);
    addLog('info', reason || 'Conversation ended. Starting fresh.');
    queryTextareaRef.current?.focus();
  };

  /** Open a finished result from a chat bubble on the requested tab. */
  const openResultTab = (sessionId: string, tab: 'map' | 'charts') => {
    const r = results.find(x => x.session_id === sessionId);
    if (!r) return;
    setSelectedResult(r);
    setShowLog(false);
    setShowChart(tab === 'charts');
    setRightPanelVisible(true);
  };

  /** Put text in the composer, bring it on screen and focus it. On phones
   *  the left panel scrolls internally, so a chip near the bottom of the
   *  sample grid would otherwise fill a textarea the user cannot see. */
  const fillComposer = (text: string) => {
    setQuery(text);
    const el = queryTextareaRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Defer focus so the smooth scroll isn't cancelled by the browser's own
    // focus-scroll, and so the mobile keyboard opens after the move.
    window.setTimeout(() => el.focus({ preventScroll: true }), 250);
  };

  const sendSuggestion = (text: string) => {
    if (loading) return;
    fillComposer(text);
  };

  // Typing indicator: show while waiting for the router/analysis and the last
  // turn has no assistant reply yet.
  const lastTurn = conversationHistory[conversationHistory.length - 1];
  const showTyping = loading && !!lastTurn && !lastTurn.response;

  // Retry-capable fetch wrapper — retries up to `retries` times on network errors
  const fetchWithRetry = async (url: string, options: RequestInit, retries = 3, delayMs = 2000): Promise<Response> => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000); // 300s timeout (5 min)
        const response = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(timeoutId);
        return response;
      } catch (err) {
        const msg = (err as Error).message || '';
        const isNetworkError = msg.includes('NetworkError') || msg.includes('Failed to fetch') || msg.includes('aborted') || msg.includes('AbortError');
        if (isNetworkError && attempt < retries) {
          console.warn(`[fetchWithRetry] attempt ${attempt}/${retries} for ${url} failed: ${msg}. Retrying in ${delayMs}ms...`);
          addLog('warning', `Network hiccup (attempt ${attempt}/${retries}), retrying...`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
          continue;
        }
        console.error(`[fetchWithRetry] FINAL failure for ${url} after ${attempt} attempts: ${msg}`);
        throw err;
      }
    }
    throw new Error('Fetch failed after all retries');
  };

  // Safe API call wrapper — catches network errors so transient backend issues don't kill the flow
  const safeApiCall = async <T,>(fn: () => Promise<T>, fallbackLabel: string, retries = 3): Promise<T | null> => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await fn();
      } catch (err) {
        console.warn(`[API] ${fallbackLabel} attempt ${attempt}/${retries} failed:`, err);
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
        }
      }
    }
    console.error(`[API] ${fallbackLabel} failed after ${retries} attempts`);
    addLog('warning', `${fallbackLabel} temporarily unavailable — continuing anyway`);
    return null;
  };

  const notifyCreditsChanged = (credits?: number) => {
    window.dispatchEvent(new CustomEvent('lleo:credits-changed', { detail: { credits } }));
  };

  const pollAnalysisStatus = async (sessionId: string, query: string) => {
    const maxPolls = 120;
    let pollCount = 0;
    let consecutiveErrors = 0;

    const poll = async () => {
      try {
        pollCount++;
        addLog('info', `Checking analysis status... (${pollCount}/${maxPolls})`);

        const response = await fetchWithRetry(`${GCP_BACKEND_URL}/status/${sessionId}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
        }, 3, 5000);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Status check failed:', response.status, errorText);
          throw new Error(`Status check failed: ${response.status} - ${errorText.substring(0, 100)}`);
        }

        consecutiveErrors = 0; // Reset on success
        const statusData: StatusData = await response.json();
        console.log('Status response:', statusData);

        if (statusData.status === 'completed') {
          addLog('success', `Analysis completed! Session ID: ${sessionId}`);
          addLog('info', `Analysis type: ${statusData.analysis_type}`);
          addLog('info', `Location: ${statusData.location || 'Unknown'}`);

          // ── Show results FIRST, then handle Supabase operations in background ──
          const newResult: AnalysisResult = {
            id: Date.now(),
            session_id: sessionId,
            query: query,
            analysis_type: statusData.analysis_type || 'Unknown',
            location: statusData.location || 'N/A',
            created_at: new Date(),
            location_lat: statusData.result?.metadata?.latitude,
            location_lng: statusData.result?.metadata?.longitude,
          };

          setResults(prev => [newResult, ...prev]);
          setSelectedResult(newResult);
          setShowChart(false);
          setShowLog(false);
          setQuery('');
          setLoading(false);
          setProgressText('');
          addLog('success', 'Analysis results ready to view!');

          const atype = statusData.analysis_type || 'default';
          const isAnim = atype === 'video_animation';
          const secs = statusData.result?.processing_time;
          const thinking = [...progressStepsRef.current];
          const thinkingSec = typeof secs === 'number'
            ? secs
            : runStartedAtRef.current ? (Date.now() - runStartedAtRef.current) / 1000 : undefined;
          setProgressSteps([]);
          const locPart = statusData.location ? ` for **${statusData.location}**` : '';
          const timePart = typeof secs === 'number' ? ` in ${Math.round(secs)}s` : '';
          setConversationHistory(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && (last.response === 'Processing...' || !last.response)) {
              updated[updated.length - 1] = { ...last, response: 'Analysis completed successfully!' };
            }
            updated.push({
              query: '',
              response: `Your ${atype.replace(/_/g, ' ')} analysis${locPart} finished${timePart}. ${isAnim ? 'The animation is ready.' : 'The map and charts are ready.'} Ask a follow-up or pick one below:`,
              timestamp: new Date(),
              isCompletionMessage: true,
              kind: 'completion',
              resultSessionId: sessionId,
              suggestions: FOLLOW_UP_SUGGESTIONS[atype] || FOLLOW_UP_SUGGESTIONS.default,
              thinking,
              thinkingSec,
            });
            return updated;
          });

          // ── Credits are charged server-side; just refresh the UI balance ──
          if (typeof statusData.credits_remaining === 'number') {
            addLog('success', `Analysis saved. Credits remaining: ${statusData.credits_remaining}`);
            notifyCreditsChanged(statusData.credits_remaining);
          } else {
            notifyCreditsChanged();
          }

        } else if (statusData.status === 'failed') {
          setLoading(false);
          setProgressText('');
          setProgressSteps([]);
          const errorMsg = statusData.error || 'Analysis failed';
          failTurn(`⚠️ ${errorMsg}`);
          addLog('error', `Analysis failed: ${errorMsg}`);

        } else if (statusData.status === 'processing') {
          const step = statusData.message || (typeof statusData.progress === 'string' ? statusData.progress : '');
          if (step) {
            pushStep(step);
            addLog('info', step);
          }

          if (pollCount < maxPolls) {
            setTimeout(poll, 5000);
          } else {
            setLoading(false);
            setProgressText('');
            failTurn('⏳ This analysis is taking longer than expected. It may still finish — check "My Analyses" in a minute.');
            addLog('error', 'Analysis timed out after 10 minutes');
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
        consecutiveErrors++;

        if ((err as Error).message.includes('NetworkError') || (err as Error).message.includes('Failed to fetch')) {
          addLog('warning', `Network error (${consecutiveErrors}): ${(err as Error).message} - Retrying...`);
        } else {
          addLog('warning', `Error checking status: ${(err as Error).message}`);
        }

        if (pollCount < maxPolls && consecutiveErrors < 10) {
          const retryDelay = (err as Error).message.includes('NetworkError') ? 10000 : 5000;
          setTimeout(poll, retryDelay);
        } else {
          setLoading(false);
          setProgressText('');
          failTurn('⚠️ I lost contact with the server while the analysis was running. It may still finish — check "My Analyses" in a moment.');
          addLog('error', `Polling stopped after ${pollCount} attempts / ${consecutiveErrors} consecutive errors`);
        }
      }
    };

    poll();
  };

  const handleConversationalAnalyze = async () => {
    if (!user) {
      setShowAuthModal(true);
      setAuthView('signup');
      return;
    }

    if (loading) return;

    if (!query.trim()) {
      setError('Please enter an analysis query');
      setInputNotice('');
      addLog('error', 'Missing analysis query');
      return;
    }

    setLoading(true);
    setRightPanelVisible(true);
    setError('');
    setInputNotice('');
    setProgressText('');
    setProgressSteps([]);
    runStartedAtRef.current = Date.now();

    const currentQuery = query.trim();
    // Clear the box immediately so the UI feels responsive; the text is already
    // in the transcript bubble, and the Log tab is only auto-opened for real runs.
    setQuery('');

    setConversationHistory(prev => [...prev, {
      query: currentQuery,
      response: '',
      timestamp: new Date()
    }]);

    try {
      let isNewConversation = !conversationMode || !conversationSessionId;
      const buildForm = (fresh: boolean) => {
        const fd = new FormData();
        if (fresh) {
          fd.append('query', currentQuery);
          fd.append('user_id', user.id);
          if (user.email) fd.append('user_email', user.email);
        } else {
          fd.append('session_id', conversationSessionId!);
          fd.append('message', currentQuery);
        }
        return fd;
      };

      addLog('info', isNewConversation ? 'Starting new conversation...' : 'Continuing conversation...');

      let response = await fetchWithRetry(`${GCP_BACKEND_URL}${isNewConversation ? '/chat/start' : '/chat/continue'}`, {
        method: 'POST',
        body: buildForm(isNewConversation),
        headers: { 'Accept': 'application/json' }
      });

      // The backend keeps conversation state in memory; after a redeploy or a
      // long pause the session is gone (404).  Recover transparently by
      // starting a fresh conversation with the same message instead of failing.
      if (!isNewConversation && response.status === 404) {
        addLog('warning', 'Previous session expired — starting a new one with your message');
        isNewConversation = true;
        response = await fetchWithRetry(`${GCP_BACKEND_URL}/chat/start`, {
          method: 'POST',
          body: buildForm(true),
          headers: { 'Accept': 'application/json' }
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Request failed: ${errorText.substring(0, 200)}`);
      }

      const data = await response.json();

      setConversationMode(true);
      setConversationSessionId(data.session_id);
      setConversationContext(data.context);

      const needsClarification = data.status === 'needs_clarification' || data.needs_clarification === true;
      setAssistantReply(
        data.message || 'Processing...',
        needsClarification ? { kind: 'notice' } : {}
      );

      addLog('success', `Response: ${data.message}`);

      if (needsClarification) {
        setInputNotice(data.message || 'Please provide a little more detail so I can run this analysis.');
        addLog('warning', 'Clarification needed');
        setLoading(false);
        queryTextareaRef.current?.focus();
        return;
      }

      if (data.status === 'general_answer') {
        addLog('success', 'General/informational answer provided (no analysis needed)');
        setLoading(false);
        queryTextareaRef.current?.focus();
        return;
      }

      if (data.is_conversational) {
        addLog('success', 'Follow-up question answered (no analysis needed)');
        setLoading(false);
        queryTextareaRef.current?.focus();
        return;
      }

      if (data.status === 'complete' || data.status === 'ready') {
        addLog('info', 'Query understood. Executing analysis...');
        setShowLog(true);
        pushStep('Starting analysis…');

        // Credit check (backend is the source of truth; it also enforces this server-side)
        const creditData = await safeApiCall(() => fetchCredits(user.id), 'Credit check', 3);

        // Only block if we CONFIRMED zero credits. If the check fails, let the backend decide.
        if (creditData && creditData.credits < 1) {
          failTurn('💳 You have no credits left. Please top up on the Pricing page to run this analysis.');
          addLog('error', 'Insufficient credits');
          setLoading(false);
          setShowLog(false);
          return;
        }
        if (!creditData) {
          addLog('warning', 'Could not verify credits — proceeding with analysis');
        }

        addLog('info', 'Starting analysis execution...');
        const analyzeResponse = await fetchWithRetry(`${GCP_BACKEND_URL}/chat/analyze`, {
          method: 'POST',
          body: new URLSearchParams({ session_id: data.session_id }),
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });

        if (analyzeResponse.status === 402) {
          failTurn('💳 You have no credits left. Please top up on the Pricing page to run this analysis.');
          addLog('error', 'Insufficient credits');
          setLoading(false);
          setShowLog(false);
          notifyCreditsChanged();
          return;
        }

        if (!analyzeResponse.ok) {
          const t = await analyzeResponse.text().catch(() => '');
          throw new Error(t ? `Analysis could not start: ${t.substring(0, 160)}` : 'Analysis could not start');
        }

        const analyzeData = await analyzeResponse.json();
        addLog('success', `Analysis started! Session ID: ${analyzeData.session_id}`);

        // Use currentQuery (captured at top of function) to avoid stale closure
        pollAnalysisStatus(analyzeData.session_id, currentQuery);
        return;
      }

      // Unknown status — don't leave the user hanging on a spinner.
      setLoading(false);

    } catch (err) {
      console.error('Conversational analysis error:', err);
      const errMsg = (err as Error).message || 'Unknown error';
      const isNetwork = errMsg.includes('NetworkError') || errMsg.includes('Failed to fetch') || errMsg.includes('AbortError');
      failTurn(isNetwork
        ? '📶 Network connection issue. Please check your internet and send the message again.'
        : `⚠️ ${errMsg}`);
      // Give the text back so the user can resend with one click.
      setQuery(currentQuery);
      addLog('error', `Error: ${errMsg}`);
      setLoading(false);
      setProgressText('');
      setProgressSteps([]);
    }
  };

  const handleDownload = async () => {
    if (!selectedResult) return;

    try {
      const response = await fetch(`${GCP_BACKEND_URL}/results/${selectedResult.session_id}/download`);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analysis_${selectedResult.session_id}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download results: ' + (err as Error).message);
    }
  };

  const renderLogContent = () => (
    <div className="log-container">
      <div className="log-header">
        <div className="log-controls">
          <label className="log-toggle">
            <input
              type="checkbox"
              checked={autoFetchLogs}
              onChange={(e) => setAutoFetchLogs(e.target.checked)}
            />
            <span>Auto-refresh (2s)</span>
          </label>
          <span className="log-count">{logs.length} logs</span>
        </div>
        <button
          onClick={() => setLogs([])}
          className="btn btn-secondary btn-small"
        >
          Clear Logs
        </button>
      </div>
      <div className="log-content log-content-scrollable" ref={logContainerRef}>
        {logs.length === 0 ? (
          <div className="log-empty">
            <div className="empty-icon">📋</div>
            <p>{loading ? loadingSteps[loadingStepIndex] : `Logs are ready. Auto-refresh is ${autoFetchLogs ? 'enabled' : 'disabled'}.`}</p>
          </div>
        ) : (
          <div className="log-entries">
            {logs.map((log, index) => (
              <div key={index} className={`log-entry log-${log.level}`}>
                <span className="log-timestamp">{log.displayTime}</span>
                <span className="log-level">{log.level.toUpperCase()}</span>
                <span className="log-message">{log.message}</span>
                {log.source && <span className="log-source">[{log.source}]</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const isInitialView = !rightPanelVisible && results.length === 0 && conversationHistory.length === 0;
  // Phones: the stacked 50/50 split is unusable for a map, so a selected
  // result opens as a full-screen sheet (same as My Analyses) and the chat
  // keeps the whole screen otherwise.
  const viewerFullscreen = isPhone && !!selectedResult && !loading;

  return (
    <div className={`landing-page-split ${rightPanelVisible ? 'show-right-panel' : ''} ${viewerFullscreen ? 'viewer-fullscreen' : ''}`}>
      <div className={`left-panel ${isInitialView ? 'initial-layout' : ''}`}>
        {isInitialView && (
          <div className="hero-section">
            <span className="hero-eyebrow">
              <span className="hero-wordmark">LLEO</span>
              <span className="hero-eyebrow-sep" aria-hidden="true">·</span>
              Large Language Models for Earth Observation
            </span>
            <h1 className="hero-title">What would you like to analyze?</h1>
            <p className="subtitle">
              Ask about vegetation, air quality, water, weather or change over time, anywhere on Earth.
            </p>
          </div>
        )}

        {conversationHistory.length > 0 && conversationHistory.some(item => item.query || item.response) && (
          <div className="conversation-history" ref={conversationHistoryRef} aria-live="polite">
            {conversationHistory.map((item, index) => {
              const isLast = index === conversationHistory.length - 1;
              const isAnim = selectedResult?.analysis_type === 'video_animation'
                && selectedResult.session_id === item.resultSessionId;
              return (
                <div key={index} className="history-item">
                  {item.query && (
                    <div className="history-query-row">
                      <div className="history-query-bubble">
                        {item.query}
                      </div>
                    </div>
                  )}

                  {item.response && (
                    <div className="history-response-row">
                      <div className="history-response-label">
                        LLEO:
                      </div>
                      <div className={`history-response-bubble ${item.kind ? `bubble-${item.kind}` : ''} ${item.thinking && item.thinking.length > 0 ? 'bubble-wide' : ''}`}>
                        {item.thinking && item.thinking.length > 0 && (
                          <ThinkingSteps steps={item.thinking} running={false} durationSec={item.thinkingSec} />
                        )}
                        <div>{parseMarkdownBold(item.response)}</div>

                        {item.kind === 'completion' && item.resultSessionId && (
                          <div className="bubble-actions">
                            <button type="button" className="bubble-action" onClick={() => openResultTab(item.resultSessionId!, 'map')}>
                              {isAnim ? '🎬 View animation' : '🗺️ View map'}
                            </button>
                            <button type="button" className="bubble-action" onClick={() => openResultTab(item.resultSessionId!, 'charts')}>
                              {isAnim ? '🖼️ View preview' : '📊 View charts'}
                            </button>
                          </div>
                        )}

                        {item.kind === 'error' && isLast && !loading && (
                          <div className="bubble-actions">
                            {query.trim() && (
                              <button type="button" className="bubble-action" onClick={handleConversationalAnalyze}>↻ Try again</button>
                            )}
                            <button type="button" className="bubble-action bubble-action-muted" onClick={() => startNewTopic()}>🔄 New topic</button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {item.suggestions && item.suggestions.length > 0 && isLast && !loading && (
                    <div className="suggestion-row">
                      {item.suggestions.map((s) => (
                        <button key={s} type="button" className="suggestion-chip" onClick={() => sendSuggestion(s)} title={s}>
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {(showTyping || (loading && progressText)) && (
              <div className="history-item">
                <div className="history-response-row">
                  <div className="history-response-label">LLEO:</div>
                  <div className={`history-response-bubble bubble-typing ${progressSteps.length > 0 ? 'bubble-wide' : ''}`} aria-label="LLEO is working">
                    {progressSteps.length > 0 ? (
                      <ThinkingSteps steps={progressSteps} running />
                    ) : progressText ? (
                      <span className="typing-progress"><span className="spinner spinner-small"></span>{progressText}</span>
                    ) : (
                      <span className="typing-dots"><span></span><span></span><span></span></span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="input-section">
          <div className="query-input-box">
            <textarea
              ref={queryTextareaRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (error) setError('');
                if (inputNotice) setInputNotice('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!loading && query.trim()) {
                    handleConversationalAnalyze();
                  }
                }
              }}
              placeholder={conversationMode ? "Ask a follow-up question or provide more details..." : "Describe your geospatial analysis request..."}
              className="query-textarea"
              rows={1}
            />

            <button
              onClick={handleConversationalAnalyze}
              className="btn-send-inline"
              disabled={loading || !query.trim()}
              title={loading ? 'Working…' : 'Send (Enter)'}
              aria-label={loading ? 'Working' : 'Send'}
            >
              {loading ? <span className="spinner spinner-small"></span> : '➤'}
            </button>
          </div>

          {conversationMode && !isInitialView && (
            <div className="input-footer input-footer-chat">
              <span className="input-hint">
                {loading
                  ? 'Working on it — you can keep reading while it runs.'
                  : inputNotice
                    ? '💬 Reply above to continue — I need a bit more detail.'
                    : 'Follow-ups keep the same location and period unless you change them. Enter to send, Shift+Enter for a new line.'}
              </span>
            </div>
          )}

          {isInitialView && (
            <div className="sample-questions">
              <p className="sample-title">Try asking</p>
              <div className="sample-grid">
                {SAMPLE_QUESTIONS.map((sample) => (
                  <button
                    key={sample.text}
                    onClick={() => fillComposer(sample.text)}
                    className="sample-chip"
                    title={sample.text}
                  >
                    <span className="sample-chip-label">{sample.domain}</span>
                    <span className="sample-chip-query">{sample.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {conversationMode && (
            <button
              onClick={() => startNewTopic()}
              className="btn btn-secondary btn-small btn-new-topic"
              title="Start a new conversation on a different topic"
              disabled={loading}
            >
              🔄 New Topic
            </button>
          )}
        </div>
      </div>

      <div className="right-panel">
        <div className="results-content">
          {loading && !showLog && (
            <div className="result-loading">
              <div className="loading-animation">
                <div className="spinner-large"></div>
                <p>{progressText || loadingSteps[loadingStepIndex]}</p>
                <p className="loading-detail">Analysis is running. Switch to Log tab for live updates.</p>
              </div>
            </div>
          )}

          {!selectedResult && results.length === 0 && !showLog && (
            <div className="empty-results">
              <div className="empty-icon">🌐</div>
              <h4>Try a featured analysis</h4>
              <p>Run one of these examples to instantly generate a real map and chart.</p>
              <div className="demo-result-grid">
                <button className="demo-result-card" onClick={() => fillComposer('NDVI analysis near Los Angeles, California from 2023 to 2024')}>
                  <span className="demo-title">Vegetation Trend</span>
                  <span className="demo-subtitle">Los Angeles • NDVI • 2023-2024</span>
                </button>
                <button className="demo-result-card" onClick={() => fillComposer('Air quality trends in Delhi, India over the past 3 months')}>
                  <span className="demo-title">Air Quality Snapshot</span>
                  <span className="demo-subtitle">Delhi • Pollutants • Last 3 months</span>
                </button>
                <button className="demo-result-card" onClick={() => fillComposer('Water quality assessment of Lake Erie')}>
                  <span className="demo-title">Water Quality Check</span>
                  <span className="demo-subtitle">Lake Erie • Turbidity & indices</span>
                </button>
                <button className="demo-result-card" onClick={() => fillComposer('Satellite timelapse animation of Dubai airport from March 2025 to Jun 2025')}>
                  <span className="demo-title">Satellite Animation</span>
                  <span className="demo-subtitle">Dubai Airport • Timelapse • Mar-Jun 2025</span>
                </button>
              </div>
            </div>
          )}

          {selectedResult && (
            <div className="result-viewer">
              <div className="result-viewer-header">
                <div>
                  <h4>{selectedResult.query}</h4>
                  <p className="result-meta">
                    {selectedResult.analysis_type} • {selectedResult.location} •
                    {new Date(selectedResult.created_at).toLocaleString()}
                  </p>
                  {(selectedResult.location_lat != null && selectedResult.location_lng != null) && (
                    <p className="result-meta result-coords">
                      📍 {Math.abs(selectedResult.location_lat).toFixed(4)}°{selectedResult.location_lat >= 0 ? 'N' : 'S'},{' '}
                      {Math.abs(selectedResult.location_lng).toFixed(4)}°{selectedResult.location_lng >= 0 ? 'E' : 'W'}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedResult(null)}
                  className="btn-close-viewer"
                  title={isPhone ? 'Back to chat' : 'Close viewer'}
                  aria-label={isPhone ? 'Back to chat' : 'Close viewer'}
                >
                  {isPhone ? '←' : '×'}
                </button>
              </div>

              <div className="viewer-tabs">
                {selectedResult.analysis_type === 'video_animation' ? (
                  <>
                    <button
                      onClick={() => {
                        setShowLog(false);
                        setShowChart(false);
                      }}
                      className={`viewer-tab ${!showLog && !showChart ? 'active' : ''}`}
                    >
                      Animation
                    </button>
                    <button
                      onClick={() => {
                        setShowLog(false);
                        setShowChart(true);
                      }}
                      className={`viewer-tab ${!showLog && showChart ? 'active' : ''}`}
                    >
                      Preview
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setShowLog(false);
                        setShowChart(false);
                      }}
                      className={`viewer-tab ${!showLog && !showChart ? 'active' : ''}`}
                    >
                      Map View
                    </button>
                    <button
                      onClick={() => {
                        setShowLog(false);
                        setShowChart(true);
                      }}
                      className={`viewer-tab ${!showLog && showChart ? 'active' : ''}`}
                    >
                      Charts
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowLog(true)}
                  className={`viewer-tab ${showLog ? 'active' : ''}`}
                >
                  Log
                </button>
              </div>

              <div className="result-viewer-content">
                {showLog ? (
                  renderLogContent()
                ) : selectedResult.analysis_type === 'video_animation' ? (
                  !showChart ? (
                    <img
                      src={`${GCP_BACKEND_URL}/results/${selectedResult.session_id}/animation`}
                      alt="Satellite Timelapse Animation"
                      className="result-chart-img"
                      style={{ maxWidth: '100%', borderRadius: '8px' }}
                    />
                  ) : (
                    <img
                      src={`${GCP_BACKEND_URL}/results/${selectedResult.session_id}/chart`}
                      alt="Animation Preview"
                      className="result-chart-img"
                    />
                  )
                ) : !showChart ? (
                  <iframe
                    src={`${GCP_BACKEND_URL}/results/${selectedResult.session_id}/map`}
                    className="result-iframe"
                    title="Analysis Map"
                  />
                ) : (
                  <img
                    src={`${GCP_BACKEND_URL}/results/${selectedResult.session_id}/chart`}
                    alt="Analysis Charts"
                    className="result-chart-img"
                  />
                )}
              </div>

              {!showLog && (
                <div className="result-viewer-actions">
                  <a
                    href={selectedResult.analysis_type === 'video_animation'
                      ? `${GCP_BACKEND_URL}/results/${selectedResult.session_id}/animation`
                      : `${GCP_BACKEND_URL}/results/${selectedResult.session_id}/map`}
                    className="btn btn-secondary btn-small"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedResult.analysis_type === 'video_animation' ? 'Open Animation in New Tab' : 'Open Map in New Tab'}
                  </a>
                  <button
                    onClick={handleDownload}
                    className="btn btn-primary btn-small"
                  >
                    Download Results
                  </button>
                </div>
              )}
            </div>
          )}

          {!selectedResult && showLog && (
            <div className="result-viewer">
              <div className="viewer-tabs">
                <button
                  onClick={() => {
                    if (!loading) {
                      setShowLog(false);
                      setShowChart(false);
                    }
                  }}
                  className={`viewer-tab ${!showLog && !showChart ? 'active' : ''}`}
                  disabled={loading}
                  style={{ opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  Map View
                </button>
                <button
                  onClick={() => {
                    if (!loading) {
                      setShowLog(false);
                      setShowChart(true);
                    }
                  }}
                  className={`viewer-tab ${!showLog && showChart ? 'active' : ''}`}
                  disabled={loading}
                  style={{ opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  Charts
                </button>
                <button
                  className={`viewer-tab ${showLog ? 'active' : ''}`}
                >
                  Log
                </button>
              </div>
              <div className="result-viewer-content log-viewer-content">
                {renderLogContent()}
              </div>
            </div>
          )}

          {!selectedResult && results.length > 0 && !showLog && (
            <div className="results-list">
              {results.map((result) => (
                <div
                  key={result.id}
                  className="result-card"
                  onClick={() => setSelectedResult(result)}
                >
                  <div className="result-header-card">
                    <span className="result-badge">{result.analysis_type}</span>
                    <span className="result-time">
                      {new Date(result.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                  <h4 className="result-query">{result.query}</h4>
                  <p className="result-location">{result.location}</p>
                  <button className="btn-view-inline">
                    View Results
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
